import { Injectable, NgZone } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Subject, takeUntil } from 'rxjs';
import LineaTablaClass from '../model/api/inspecciones/lineaTablaClass';
import { AlertService } from './alert.service';
import ValoresClass from '../model/api/datos/valoresClass';
import { LogicData } from '../model/api/inspecciones/inspeccionLogic';
import InspeccionClass from '../model/api/inspecciones/inspeccionClass';
import GruposClass from '../model/api/inspecciones/gruposClass';
import ParametrosClass from '../model/api/inspecciones/parametrosClass';
import DefectoClass from '../model/api/inspecciones/defectoClass';

@Injectable({
  providedIn: 'root'
})
export class InspeccionFormsService {

  //#region Propiedades

  public AllParametros: ValoresClass[]
  public DiccionarioParametrosAutorrellenableFavorable: Record<number, boolean> = {}

  //#endregion

  //#region Constructor

  constructor(
    private fb: FormBuilder,
    private alertService: AlertService,
    private zone: NgZone
  ) { }

  //#endregion

  //#region Forms

  public generateMainForm(): FormGroup {
    return this.fb.group({
      LocalId: [0],
      Id: [0],
      Codigo: [''],
      NumOferta: [''],
      NumPedido: [''],
      NumInforme: [''],
      Inspeccion: [''],
      FechaInicio: [''],
      FechaFin: [''],
      Telefono: [''],
      Email: ['', Validators.email],
      ListaGrupos: this.fb.array([]),
      StartupRan: [false],
      AreaId: [0],
      Area: [''],
      UploadFotosToken: [''],
      NombreCliente: [''],
      ListaFicheros: this.fb.array([]),
      DireccionEmplazamiento: [''],
      OtraInformacion: [''],
      Descargada: [false],
      AlgunDocumentoGenerado: [false],
      AlgunDocumentoFirmado: [false],
      DescartadaPorElTecnico: [false],
      Selected: [false],
      NoGuardadoEnServidor: [false],
      SeHaAplicadoLaLogica: [false],
      ListaParametrosConfig: [[] as string[]],
      FirmaConformidadNoGuardada: [false],
      FirmaConservadorNoGuardada: [false],
      FirmaInspectorNoGuardada: [false],
      Expediente: [''],
      MotivoDescarte: ['']
    })
  }

  public createReactiveFormsInspeccion(ordenTrabajo: InspeccionClass, destroy$: Subject<void>): FormGroup {
    // Creamos el ReaciveForms:
    let inspeccionFormGroup = this.generateMainForm()
    ordenTrabajo.ListaGrupos.forEach((grupoOrden) => {
      let grupo = this.AddGrupo(inspeccionFormGroup)
      grupoOrden.ListaParametros.forEach((parametroOrden) => {
        let parametro = this.AddParametro(grupo, destroy$)
        parametroOrden.DefectosEstablecidos?.forEach((defectoEstablecido) => {
          this.AddDefectoEstablecido(parametro.get('DefectosEstablecidos') as FormArray)
        })
        parametroOrden.DefectosMaestro?.forEach((defectoMaestro) => {
          this.AddDefectoMaestro(parametro.get('DefectosMaestro') as FormArray)
        })
        if (parametroOrden.Tipo == 'Tabla') {
          parametroOrden.Tabla.Lineas.forEach((lineaTablaOrden) => {
            let tabla = parametro.get('Tabla') as FormGroup
            let lineaTabla = this.AddLineaTabla(tabla)
            lineaTablaOrden.ListaParametros.forEach((parametroLineaOrden) => {
              this.AddParametroTabla(lineaTabla, destroy$)
            })
          })
        }
      })
    })

    return inspeccionFormGroup    
  }

  public AddGrupo(form: FormGroup): FormGroup {
    let formArray = form.get('ListaGrupos') as FormArray
    let formGroup = this.fb.group({
      Id: [0],
      Codigo: [''],
      CodigoDepende: [''],
      GrupoComprobacion: [''],
      GrupoComprobacionDepende: [''],
      ListaParametros: this.fb.array([]),
      Visible: [false],
      CampoRelacion: [''],
      Descripcion: ['']
    })
    formArray.push(formGroup)
    formArray.updateValueAndValidity()
    return formGroup
  }

  public AddFichero(form: FormGroup): FormGroup {
    let formArray = form.get('ListaFicheros') as FormArray
    let formGroup = this.fb.group({
      Id: [0],
      FileName: [''],
      ServerName: [''],
      PuedoDescargar: [false]
    })
    formArray.push(formGroup)
    formArray.updateValueAndValidity()
    return formGroup
  }

  public AddParametro(form: FormGroup, destroy$: Subject<void>): FormGroup {
    let formArray = form.get('ListaParametros') as FormArray
    let formGroup = this.fb.group({
      Obligatorio: [false],
      Orden: [0],
      Parametro: [''],
      ParametroDeCabecera: [false],
      PuedeNoAplicar: [false],
      SoloLectura: [false],
      Tabla: this.fb.group({
        Tabla: [''],
        Cabeceras: [[] as string[]],
        Lineas: this.fb.array([])
      }),
      Tipo: [''],
      Valor: [''],
      IdParametro: [0],
      IdParametroFormato: [0],
      IdParametroHistorico: [0],
      IdRegistro: [0],
      Codigo: [''],
      Visible: [true],
      DefectosEstablecidos: this.fb.array([]),
      DefectosMaestro: this.fb.array([])
    })
    formGroup.valueChanges.pipe(
      takeUntil(destroy$)
    ).subscribe(() => {
      let obligatorio = formGroup.get('Obligatorio')?.value
      let soloLectura = formGroup.get('SoloLectura')?.value
      if ((obligatorio && !soloLectura) && !formGroup.get('Valor').hasValidator(Validators.required)) {
        formGroup.get('Valor').setValidators(Validators.required)
        formGroup.get('Valor').updateValueAndValidity()
      } else if ((!(obligatorio && !soloLectura)) && formGroup.get('Valor').hasValidator(Validators.required)) {
        formGroup.get('Valor').clearValidators()
        formGroup.get('Valor').updateValueAndValidity()
      }
    })
    formArray.push(formGroup)
    formArray.updateValueAndValidity()
    return formGroup
  }

  public AddDefectoEstablecido(formArray: FormArray): FormGroup {
    let formGroup = this.fb.group({
      Codigo: [''],
      Defecto: [''],
      Id: [0],
      Observaciones: [''],
      ValoracionId: [null as number]
    })
    formArray.push(formGroup)
    formArray.updateValueAndValidity()
    return formGroup
  }

  public AddDefectoMaestro(formArray: FormArray): FormGroup {
    let formGroup = this.fb.group({
      Codigo: [''],
      Defecto: [''],
      Id: [0],
      Observaciones: [''],
      ValoracionId: [null as number]
    })
    formArray.push(formGroup)
    formArray.updateValueAndValidity()
    return formGroup
  }

  public AddLineaTabla(form: AbstractControl): FormGroup {
    let formArray = form.get('Lineas') as FormArray
    let formGroup = this.fb.group({
      ListaParametros: this.fb.array([])
    })
    formArray.push(formGroup)
    formArray.updateValueAndValidity()
    return formGroup
  }

  public AddParametroTabla(form: FormGroup, destroy$: Subject<void>): FormGroup {
    let formArray = form.get('ListaParametros') as FormArray
    let formGroup = this.fb.group({
      Obligatorio: [false],
      Orden: [0],
      Parametro: [''],
      ParametroDeCabecera: [false],
      PuedeNoAplicar: [false],
      SoloLectura: [false],
      Tipo: [''],
      Valor: [''],
      IdParametro: [0],
      IdParametroFormato: [0],
      IdParametroHistorico: [0],
      IdRegistro: [0],
      Codigo: [''],
      Visible: [true],
      DefectosEstablecidos: this.fb.array([]),
      DefectosMaestro: this.fb.array([])
    })
    
    // Nos piden también aplicar la obligatoriedad a los campos de las tablas.
    formGroup.valueChanges.pipe(
      takeUntil(destroy$)
    ).subscribe(() => {
      let obligatorio = formGroup.get('Obligatorio')?.value
      let soloLectura = formGroup.get('SoloLectura')?.value
      if ((obligatorio && !soloLectura) && !formGroup.get('Valor').hasValidator(Validators.required)) {
        formGroup.get('Valor').setValidators(Validators.required)
        formGroup.get('Valor').updateValueAndValidity()
      } else if ((!(obligatorio && !soloLectura)) && formGroup.get('Valor').hasValidator(Validators.required)) {
        formGroup.get('Valor').clearValidators()
        formGroup.get('Valor').updateValueAndValidity()
      }
    })

    formArray.push(formGroup)
    formArray.updateValueAndValidity()
    return formGroup
  }

  public async AddLineaTablaExistente(data: {
    tabla: AbstractControl<any, any>,
    campoRelacion: string,
    valorCampoRelacion: string,
    destroy$: Subject<void>
  }): Promise<FormGroup> {
    let tabla = data.tabla.get('Tabla')
    let formArray = tabla.get('Lineas') as FormArray
    let value: LineaTablaClass[] = formArray.getRawValue()
    if (value.length < 1) {
      await this.alertService.showMessage('No se ha podido crear una línea en la tabla porque la tabla está vacía.')
      return null
    }
    let formGroup = this.fb.group({
      ListaParametros: this.fb.array([])
    })
    let lineaActual = value[0]
    lineaActual.ListaParametros.forEach((p) => {
      let group = this.AddParametroTabla(formGroup, data.destroy$)
      group.patchValue(p)
      group.patchValue({
        Valor: '',
        IdRegistro: 0,
        Visible: true,
        DefectosMaestro: []
      })

      // Añadimos el maestro de defectos
      p.DefectosMaestro.forEach(q => this.AddDefectoMaestro(group.get('DefectosMaestro') as FormArray))
      group.patchValue({
        DefectosMaestro: p.DefectosMaestro
      })

      if (data.campoRelacion != null && group.get('Parametro')?.value == data.campoRelacion) {
        // Si tenemos valor, lo asignamos, sino, generamos un valor negativo.
        if (data.valorCampoRelacion != null) {
          group.patchValue({
            Valor: data.valorCampoRelacion
          })
        } else {
          let valores: number[] = [];
          (tabla.get('Lineas') as FormArray).controls.forEach(p => {
            (p.get('ListaParametros') as FormArray).controls.forEach(q => {
              if (q.get('Parametro')?.value == data.campoRelacion) {
                let value = q.get('Valor')?.value
                if (value != null && !isNaN(parseInt(value))) {
                  valores.push(parseInt(value))
                }
              }
            })
          })
          let max = 0
          if (valores.length) {
            max = Math.max(...valores)
          }
          group.patchValue({
            Valor: max + 1
          })
        }
      }

    })
    formArray.push(formGroup)
    console.log(`Formulario marcado como touched.`)
    formGroup.markAllAsTouched()
    formArray.updateValueAndValidity()
    return formGroup
  }

  //#endregion
  
  //#region Validación

  // Estas dos funciones se llaman desde las cabeceras de cada grupo en cada ciclo de detección de cambios, y
  // recorren recursivamente toda la lista de grupos resolviendo rutas con .get('...'). Con muchas materias ADR
  // (perfil de rendimiento: ~70% del tiempo) la página se atora. Durante un ciclo de detección el estado del
  // formulario no cambia, así que memorizamos el resultado y lo descartamos en cuanto se vacía la cola de
  // microtareas (es decir, nada más terminar el ciclo), de modo que ningún manejador de eventos lee un valor viejo.
  private cacheInvalidoTouched = new Map<AbstractControl, boolean>()
  private cacheAutorellenable = new Map<AbstractControl, boolean>()
  private cacheDependientes = new Map<FormArray, Map<any, FormGroup[]>>()
  private limpiezaProgramada = false

  private programarLimpiezaCache(): void {
    if (this.limpiezaProgramada) return
    this.limpiezaProgramada = true
    // Fuera de la zona de Angular: una microtarea dentro de la zona dispara otro ciclo de detección de cambios al
    // terminar, que volvería a llamar a estas funciones y a programar otra limpieza, en bucle infinito.
    this.zone.runOutsideAngular(() => {
      Promise.resolve().then(() => {
        this.cacheInvalidoTouched.clear()
        this.cacheAutorellenable.clear()
        this.cacheDependientes.clear()
        this.limpiezaProgramada = false
      })
    })
  }

  // Índice CodigoDepende -> grupos que dependen de él, calculado una vez por ciclo en lugar de filtrar la lista
  // completa por cada grupo y cada llamada recursiva.
  private gruposDependientesDe(grupo: AbstractControl<any, any>, listaGrupos: FormArray): FormGroup[] {
    let indice = this.cacheDependientes.get(listaGrupos)
    if (indice == null) {
      indice = new Map()
      for (let p of listaGrupos.controls) {
        const codigoDepende = p.get('CodigoDepende')?.value
        if (codigoDepende == null) continue
        if (!indice.has(codigoDepende)) indice.set(codigoDepende, [])
        indice.get(codigoDepende).push(p as FormGroup)
      }
      this.cacheDependientes.set(listaGrupos, indice)
    }
    const codigo = grupo.get('Codigo')?.value
    // Si el grupo no tiene código, la comparación original (undefined == undefined/null) coincidía con los
    // grupos sin CodigoDepende; se conserva ese comportamiento.
    if (codigo == null) return listaGrupos.controls.filter(p => p.get('CodigoDepende')?.value == codigo) as FormGroup[]
    return indice.get(codigo) ?? []
  }

  public esGrupoInvalidoYTouched(grupo: AbstractControl<any, any>, listaGrupos: FormArray): boolean {
    this.programarLimpiezaCache()
    const cacheado = this.cacheInvalidoTouched.get(grupo)
    if (cacheado !== undefined) return cacheado
    const resultado = this.calcularGrupoInvalidoYTouched(grupo, listaGrupos)
    this.cacheInvalidoTouched.set(grupo, resultado)
    return resultado
  }

  private calcularGrupoInvalidoYTouched(grupo: AbstractControl<any, any>, listaGrupos: FormArray): boolean {
    if (grupo.touched) {
      if (!grupo.valid) {
        return true
      }
      for (let grupoDependiente of this.gruposDependientesDe(grupo, listaGrupos)) {
        if (grupoDependiente.get('Visible')?.value) {
          if (this.esGrupoInvalidoYTouched(grupoDependiente, listaGrupos)) {
            return true
          }
        }
      }
      return false
    } else {
      return false
    }
  }

  // Me creo un diccionario que carga al cargar la inspección para almacenar qué campos contienen la opción "F".
  // Esto es necesario para optimizar el rendimiento porque esta función se dispara cientos de veces en el pintado.
  public esGrupoAutorellenableFavorable(grupo: AbstractControl<any, any>, listaGrupos: FormArray): boolean {
    this.programarLimpiezaCache()
    const cacheado = this.cacheAutorellenable.get(grupo)
    if (cacheado !== undefined) return cacheado
    const resultado = this.calcularGrupoAutorellenableFavorable(grupo, listaGrupos)
    this.cacheAutorellenable.set(grupo, resultado)
    return resultado
  }

  private calcularGrupoAutorellenableFavorable(grupo: AbstractControl<any, any>, listaGrupos: FormArray): boolean {
    if (grupo.touched) {
      if (!grupo.valid) {
        for (let control of (grupo.get('ListaParametros') as FormArray).controls) {
          if (!control.valid && (control.get('IdParametro')?.value != null)) {
            if (this.DiccionarioParametrosAutorrellenableFavorable[control.get('IdParametro')?.value] != null) {
              if (this.DiccionarioParametrosAutorrellenableFavorable[control.get('IdParametro')?.value]) {
                return true
              }
            } else {
              if ((control.get('Valor')?.value == null || control.get('Valor')?.value == '') && (control.get('Tipo')?.value == 'Opción') || control.get('Tipo')?.value == 'Opción con Valor Múltiple') {
                // Si el valor está vacío y es opción con valor múltiple, miramos en las posibles opciones si existe una con valor "F"
                let valores = this.AllParametros.filter(p => p.ParametroId == control.get('IdParametro')?.value)
                if (valores.find(p => p.Valor == 'F')) {
                  this.DiccionarioParametrosAutorrellenableFavorable[control.get('IdParametro')?.value] = true
                  return true
                } else {
                  this.DiccionarioParametrosAutorrellenableFavorable[control.get('IdParametro')?.value] = false
                }
              } else {
                this.DiccionarioParametrosAutorrellenableFavorable[control.get('IdParametro')?.value] = false
              }
            }
          }
        }
      }
      for (let grupoDependiente of this.gruposDependientesDe(grupo, listaGrupos)) {
        if (grupoDependiente.get('Visible')?.value) {
          if (this.esGrupoAutorellenableFavorable(grupoDependiente, listaGrupos)) {
            return true
          }
        }
      }
      return false
    } else {
      return false
    }
  }

  public autoRellenarFavorable(grupo: AbstractControl<any, any>, listaGrupos: FormArray): void {
    if (!grupo.valid) {
      for (let control of (grupo.get('ListaParametros') as FormArray).controls) {
        if (!control.valid && (control.get('IdParametro')?.value != null)) {
          if ((control.get('Valor')?.value == null || control.get('Valor')?.value == '') && (control.get('Tipo')?.value == 'Opción') || control.get('Tipo')?.value == 'Opción con Valor Múltiple') {
            // Si el valor está vacío y es opción con valor múltiple, miramos en las posibles opciones si existe una con valor "F"
            let valores = this.AllParametros.filter(p => p.ParametroId == control.get('IdParametro')?.value)
            if (valores.find(p => p.Valor == 'F')) {
              control.get('Valor').setValue(valores.find(p => p.Valor == 'F').Id?.toString())
            }
          }
        }
      }
    }
    let gruposDependientes = listaGrupos.controls.map(p => p as FormGroup).filter(p => p.get('CodigoDepende')?.value == grupo.get('Codigo')?.value)
    for (let grupoDependiente of gruposDependientes) {
      if (grupoDependiente.get('Visible')?.value) {
        this.autoRellenarFavorable(grupoDependiente, listaGrupos)
      }
    }
  }

  //#endregion

  //#region Lógica

  public runTableLogic = new Subject<{tableName: string, logic: (tableLines: Record<string, ParametrosClass>[]) => void}>()
  public runLogicOT = new Subject<{logic: (data: LogicData) => void}>()
  public gestionarDefectosParametro = new Subject<{parametro: string, logic: (defectosActivos: DefectoClass[], defectosMaestro: DefectoClass[]) => void}>
  public setCamposCheckDefectos = new Subject<{codigos: string[]}>()

  public async convertParamsToLogic(params: Record<string, ParametrosClass>): Promise<Record<string, ParametrosClass>> {
    let objResult: Record<string, ParametrosClass> = {}
    for (let key of Object.keys(params)) {
      let objSource = params[key]
      let parametro: ParametrosClass = JSON.parse(JSON.stringify(objSource))

      // Si el parámetro es un desplegable, cargamos el String de su valor.
      if (parametro.Tipo == 'Desplegable' || parametro.Tipo == 'Opción') {
        let params = this.AllParametros.filter(p => p.ParametroId == parametro.IdParametro)
        if (parametro.Valor != null && !isNaN(parseInt(parametro.Valor))) {
          if (params.find(p => p.Id == parseInt(parametro.Valor.toString()))) {
            parametro.Valor = params.find(p => p.Id == parseInt(parametro.Valor.toString())).Valor
          } else {
            parametro.Valor = ''
          }
        } else {
          parametro.Valor = ''
        }
      } else if (parametro.Tipo == 'Desplegable con Valor Múltiple' || parametro.Tipo == 'Opción con Valor Múltiple') {
        let params = this.AllParametros.filter(p => p.ParametroId == parametro.IdParametro)
        if (parametro.Valor != null) {
          let values = parametro.Valor.split(";")
          let paramValues: string[] = []
          values.forEach(p => {
            if (params.find(q => q.Id == parseInt(p))) {
              paramValues.push(params.find(q => q.Id == parseInt(p)).Valor)
            }
          })
          parametro.Valor = paramValues.join(";")
        } else {
          parametro.Valor = ''
        }
      }

      objResult[key] = parametro
    }
    return objResult
  }

  public async oldValueToLogic(oldValue: string, parametro: ParametrosClass): Promise<string> {
    // Si el parámetro es un desplegable, cargamos el String de su valor.
    if (parametro.Tipo == 'Desplegable' || parametro.Tipo == 'Opción') {
      let params = this.AllParametros.filter(p => p.ParametroId == parametro.IdParametro)
      if (oldValue != null && !isNaN(parseInt(oldValue))) {
        if (params.find(p => p.Id == parseInt(oldValue.toString()))) {
          return params.find(p => p.Id == parseInt(oldValue.toString())).Valor
        } else {
          return ''
        }
      } else {
        return ''
      }
    } else if (parametro.Tipo == 'Desplegable con Valor Múltiple' || parametro.Tipo == 'Opción con Valor Múltiple') {
      let params = this.AllParametros.filter(p => p.ParametroId == parametro.IdParametro)
      if (oldValue != null) {
        let values = oldValue.split(';')
        let paramValues: string[] = []
        values.forEach(p => {
          if (params.find(q => q.Id == parseInt(p))) {
            paramValues.push(params.find(q => q.Id == parseInt(p)).Valor)
          }
        })
        return paramValues.join(';')
      } else {
        return ''
      }
    }
    return oldValue
  }

  public async convertFormToLogicData(form: InspeccionClass): Promise<LogicData> {
    let logicData: LogicData = {}

    let datosOT: Record<string, any> = {}
    const keysExclude = ['AlgunDocumentoFirmado', 'AlgunDocumentoGenerado', 'ListaGrupos', 'UploadFotosToken']
    Object.keys(form).forEach(p => keysExclude.indexOf(p) == -1 ? datosOT[p] = form[p] : null)
    logicData["DatosOT"] = datosOT as any;

    form.ListaGrupos.forEach(grupo => {
      if (grupo.Codigo != null && grupo.Codigo != '') {
        grupo = JSON.parse(JSON.stringify(grupo))
        logicData[grupo.Codigo] = grupo
      }
      grupo.ListaParametros.forEach(parametro => {
        if (parametro.Codigo != null && parametro.Codigo != '') {
          parametro = JSON.parse(JSON.stringify(parametro))
          // Si el parámetro es un desplegable, cargamos el String de su valor.
          if (parametro.Tipo == 'Desplegable' || parametro.Tipo == 'Opción') {
            let params = this.AllParametros.filter(p => p.ParametroId == parametro.IdParametro)
            if (parametro.Valor != null && !isNaN(parseInt(parametro.Valor))) {
              if (params.find(p => p.Id == parseInt(parametro.Valor.toString()))) {
                parametro.Valor = params.find(p => p.Id == parseInt(parametro.Valor.toString())).Valor
              } else {
                parametro.Valor = ''
              }
            } else {
              parametro.Valor = ''
            }
          } else if (parametro.Tipo == 'Desplegable con Valor Múltiple' || parametro.Tipo == 'Opción con Valor Múltiple') {
            let params = this.AllParametros.filter(p => p.ParametroId == parametro.IdParametro)
            if (parametro.Valor != null) {
              let values = parametro.Valor.split(";")
              let paramValues: string[] = []
              values.forEach(p => {
                if (params.find(q => q.Id == parseInt(p))) {
                  paramValues.push(params.find(q => q.Id == parseInt(p)).Valor)
                }
              })
              parametro.Valor = paramValues.join(";")
            } else {
              parametro.Valor = ''
            }
          }
          logicData[parametro.Codigo] = parametro
        }
      })
    })

    return logicData
  }

  public async convertLogicToParams(data: Record<string, ParametrosClass>): Promise<Record<string, ParametrosClass>> {
    let objResult: Record<string, ParametrosClass> = {}
    for (let key of Object.keys(data)) {
      let objSource = data[key]
      let newRecordData: ParametrosClass = JSON.parse(JSON.stringify(objSource))

      // Si es un desplegable, cargamos el valor en lugar del texto.
      if (newRecordData.Tipo == 'Desplegable' || newRecordData.Tipo == 'Opción') {
        let params = this.AllParametros.filter(p => p.ParametroId == newRecordData.IdParametro)
        if (newRecordData.Valor != null && newRecordData.Valor != '') {
          if (params.find(p => p.Valor == newRecordData.Valor)) {
            newRecordData.Valor = params.find(p => p.Valor == newRecordData.Valor).Id.toString()
          } else {
            newRecordData.Valor = ''
          }
        } else {
          newRecordData.Valor = ''
        }
      } else if (newRecordData.Tipo == 'Desplegable con Valor Múltiple' || newRecordData.Tipo == 'Opción con Valor Múltiple') {
        let params = this.AllParametros.filter(p => p.ParametroId == newRecordData.IdParametro)
        if (newRecordData.Valor != null && newRecordData.Valor != '') {
          let values = newRecordData.Valor.split(";")
          let paramValues: string[] = []
          values.forEach(p => {
            if (params.find(q => q.Valor == p)) {
              paramValues.push(params.find(q => q.Valor == p).Id.toString())
            }
          })
          newRecordData.Valor = paramValues.join(";")
        } else {
          newRecordData.Valor = ''
        }
      }

      objResult[key] = newRecordData
    }
    return objResult
  }

  public async parseDataToForm(
    data: LogicData, 
    listaGrupos: FormArray,
    listaCambios: {Campo: string, Valor: string, ValorOld: string}[]
  ): Promise<void> {

    listaGrupos.controls.forEach(p => {
      let record: GruposClass = p.getRawValue()
      if (record.Codigo != null && record.Codigo != '') {
        let newRecordData: GruposClass = data[record.Codigo] as GruposClass
        if (newRecordData != null) {
          p.patchValue(newRecordData)
        }
      }
    })
    listaGrupos.controls.forEach(p => {
       let array = p.get('ListaParametros') as FormArray
       array.controls.forEach(q => {
        let record: ParametrosClass = q.getRawValue()
        if (record.Codigo != null && record.Codigo != '') {
          let newRecordData: ParametrosClass = data[record.Codigo] as ParametrosClass
          if (newRecordData != null) {

            let newValue: string = newRecordData.Valor || ''

            // Si es un desplegable, cargamos el valor en lugar del texto
            if (newRecordData.Tipo == 'Desplegable' || newRecordData.Tipo == 'Opción') {
              let params = this.AllParametros.filter(p => p.ParametroId == newRecordData.IdParametro)
              if (newRecordData.Valor != null && newRecordData.Valor != '') {
                if (params.find(p => p.Valor == newRecordData.Valor)) {
                  newRecordData.Valor = params.find(p => p.Valor == newRecordData.Valor).Id.toString()
                } else {
                  newRecordData.Valor = ''
                }
              } else {
                newRecordData.Valor = ''
              }
            } else if (newRecordData.Tipo == 'Desplegable con Valor Múltiple' || newRecordData.Tipo == 'Opción con Valor Múltiple') {
              let params = this.AllParametros.filter(p => p.ParametroId == newRecordData.IdParametro)
              if (newRecordData.Valor != null && newRecordData.Valor != '') {
                let values = newRecordData.Valor.split(";")
                let paramValues: string[] = []
                values.forEach(p => {
                  if (params.find(q => q.Valor == p)) {
                    paramValues.push(params.find(q => q.Valor == p).Id.toString())
                  }
                })
                newRecordData.Valor = paramValues.join(";")
              } else {
                newRecordData.Valor = ''
              }
            }

            if ((q.get('Valor').value || '') != (newRecordData.Valor || '')) {
              let oldValue = q.get('Valor').value
              if (newRecordData.Tipo == 'Desplegable' || newRecordData.Tipo == 'Opción') {
                let params = this.AllParametros.filter(p => p.ParametroId == newRecordData.IdParametro)
                if (oldValue != null && oldValue != '') {
                  if (params.find(p => p.Id == oldValue)) {
                    oldValue = params.find(p => p.Id == oldValue).Valor
                  } else {
                    oldValue = ''
                  }
                } else {
                  oldValue = ''
                }
              } else if (newRecordData.Tipo == 'Desplegable con Valor Múltiple' || newRecordData.Tipo == 'Opción con Valor Múltiple') {
                let params = this.AllParametros.filter(p => p.ParametroId == newRecordData.IdParametro)
                if (oldValue != null && oldValue) {
                  let values = oldValue.split(";")
                  let paramValues: string[] = []
                  values.forEach(p => {
                    if (params.find(q => q.Id == p)) {
                      paramValues.push(params.find(q => q.Id == p).Valor)
                    }
                  })
                  oldValue = paramValues.join(";")
                } else {
                  oldValue = ''
                }
              }
              listaCambios.push({Campo: newRecordData.Codigo, Valor: newValue, ValorOld: oldValue})
            }

            q.patchValue(newRecordData)
          }
        }
       })
    })
  }

  //#endregion

}
